<template>
  <!-- prettier-ignore -->
  <div
    :class="classes"
    :app-version="$version"
    :app-name="$options.name"
    :app-language="$i18n.locale">

    <ovp-widget-loading
      v-if="isLoading"
      class="entry-loading"
    />

    <div class="entry__wrapper">
      <div class="entry__content">
        <div
          v-if="title"
          class="entry__title"
          @click="navigate">
          {{ computedTitle }}
        </div>

        <div
          v-if="tiny"
          class="entry__button">
          <ovp-button
            primary
            type="button"
            @click="navigate"
            v-text="button"
          />
        </div>

        <div v-else>
          <ovp-entry-form
            v-model="formData"
            :basket="basket"
            :button-text="button"
            :theme="theme"
            @submit="createBasket"
          />

          <a
            v-if="hasBasket"
            class="entry__restart"
            @click="restart">{{ $t('entry.restart') }}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from '@/setup'

import { v4 as uuidv4 } from 'uuid'
import { mapState } from 'vuex'

import OvpEntryForm from '@/modules/EntryWidget/components/Form'
import OvpWidgetLoading from '@/components/Container/WidgetLoading'

import { ApplicationHelper, BasketHelper, LogHelper, SessionHelper } from '@/helpers'

import BasketMixin from '@/mixins/Basket'

import {
  CHANNEL_ENTRYWIDGET,
  DEFAULT_MASTER_TREATY,
  NOTIFICATION_ERROR,
  STATE_LOADING,
  STATE_READY,
} from '@/config/constants'

import { EVENT_READY, SOURCE_BACKEND, SOURCE_ENTRY_WIDGET } from '@/config/events'

export default Vue.extend({
  name: 'EntryWidget',

  mixins: [BasketMixin],

  components: {
    OvpEntryForm,
    OvpWidgetLoading,
  },

  props: {
    buttonText: {
      type: String,
      default: '',
    },

    header: {
      type: Boolean,
      default: false,
    },

    source: {
      type: String,
      default: SOURCE_ENTRY_WIDGET,
    },

    theme: {
      type: String,
      default: 'dark',
    },

    title: {
      type: String,
      default: '',
    },

    tiny: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      state: STATE_LOADING,
    }
  },

  created() {
    this.$EventBus.$once(EVENT_READY, () => {
      this.state = STATE_READY
    })
  },

  computed: {
    ...mapState(['basket']),

    baseUrl() {
      return ApplicationHelper.getDvpUrl(this.$i18n.locale)
    },

    button() {
      if (this.hasBasket) {
        return this.$t('entry.resume')
      } else {
        return this.buttonText ? this.buttonText : this.$t('entry.button')
      }
    },

    classes() {
      return {
        entry: true,
        [`entry--${this.theme}`]: true,
        'entry--tiny': this.tiny,
        header: this.header,
      }
    },

    computedTitle() {
      if (this.hasBasket) {
        return this.$t('entry.return')
      } else {
        return this.title
      }
    },

    formData() {
      const person = this.basket?.persons[0] ?? false

      if (!person) return {}

      const personData = person.personData
      return {
        dateOfBirth: personData.dateOfBirth,
        gender: personData.gender,
        location: this.basket.address,
        personId: person.personId,
      }
    },

    hasBasket() {
      return this.basket?.basketId && this.basket.persons.length > 0
    },

    isLoading() {
      return this.state === STATE_LOADING
    },
  },

  methods: {
    createBasket(data) {
      const resume = !!data.personId
      this.state = STATE_LOADING

      if (resume) {
        this.resume()
      } else {
        const personId = data.personId ?? uuidv4()
        const payload = {
          address: data.location,
          channel: CHANNEL_ENTRYWIDGET,
          language: this.$i18n.locale.toUpperCase(),
          masterTreaty: DEFAULT_MASTER_TREATY,
          persons: [
            {
              personId,
              personData: {
                dateOfBirth: data.dateOfBirth,
                gender: data.gender,
              },
            },
          ],
        }
        return this.startBasketSession(payload)
          .then(() => this.navigate())
          .catch(error => {
            if (error?.source !== SOURCE_BACKEND) {
              this.$notification.error(error)
            }
          })
      }
    },

    navigate() {
      window.open(this.baseUrl, '_self').focus()
    },

    restart() {
      return ApplicationHelper.restartApplication().then(() => this.navigate())
    },

    resume() {
      this.navigate()
    },

    startBasketSession(payload) {
      const createBasket = basket => this.$backend.ovp.createBasket(basket)

      return this.$session
        .start(createBasket, payload)
        .then(basket => SessionHelper.restore(basket))
        .then(basket => BasketHelper.updateBasketSession(basket))
        .catch(error => {
          LogHelper.error(error)

          this.$notification.add(this.$t('error.NETWORK'), NOTIFICATION_ERROR, 300000)
        })
    },
  },
})
</script>

<style name="global" scoped>
* {
  & >>> button {
    font-weight: bold;
  }

  & >>> button,
  input,
  select {
    font-family: Open Sans, sans-serif !important;
  }
}
</style>

<style name="mobile" scoped>
.entry {
  & >>> * {
    font-family: Open Sans, sans-serif;
  }

  position: relative;
  padding: 20px 0;
  margin-left: calc(-1 * var(--sm-body-padding));
  margin-right: calc(-1 * var(--sm-body-padding));

  background-color: var(--c-dark-green);
  color: var(--c-white);

  button {
    margin-bottom: 0;
  }

  &__wrapper {
    margin: 0 15px;
  }

  &__title {
    font-weight: bold;
    font-size: 20px;
    line-height: 20px;
    margin: 15px 0 5px 0;
    cursor: pointer;
  }

  &__grid {
    display: flex;
    flex-wrap: wrap;
    margin: -25px;
    padding: 20px 15px;
  }

  &__cell {
    width: 50%;
    max-width: 50%;
    padding: 15px 10px;

    &--large {
      width: 100%;
      max-width: 100%;
    }
  }

  &__restart {
    cursor: pointer;
    display: inline-block;
    width: 300px;
    color: var(--c-original-green);
    border-bottom: 0 !important;
    font-weight: bold;

    &:hover {
      color: var(--c-sanitas-green);
    }
  }

  &--tiny.entry {
    .entry {
      &__title {
        font-size: 20px;
        line-height: 25px;
      }

      &__button {
        padding: 15px 0;
      }
    }
  }

  &--light {
    color: var(--c-black);
    background-color: var(--c-white);

    .entry {
      &__field {
        background: inherit;
      }

      &__skeleton {
        height: 55px;
        border-bottom: 1px solid var(--c-cool-gray);
      }
    }
  }
}
</style>

<style name="tablet" scoped>
/* Special handling for tablet on .com */
@media (--sm) {
  .entry {
    &--tiny.entry {
      .entry {
        &__title {
          margin: auto 0;
        }

        &__button {
          width: 300px;
          margin: 0 0 0 auto;
          padding-bottom: 0;

          .button {
            width: 100%;
            min-width: 300px;
          }
        }
      }
    }
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .entry {
    &--tiny.entry {
      padding: 20px 0;

      .entry {
        &__content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 50px;

          &:after {
            content: '';
            min-height: inherit;
            font-size: 0;
          }
        }

        &__title {
          flex: 1 0 50%;
          margin: auto 0;
        }

        &__button {
          flex: 0 0 300px;
          margin: auto 0 auto 20px;
          padding: 0;
        }
      }
    }

    &-buttons {
      display: flex;
      flex-direction: row;
      padding: 0 20px;

      button {
        flex: 1 1 50%;

        &:first-child {
          margin-right: 10px;
        }

        &:last-child {
          margin-left: 10px;
        }
      }
    }
  }
}
</style>

<style name="desktop-com-handling" scoped>
/* Special handling for desktop on .com */
/* you need 1000px for a clean cut between full form size */
@media (min-width: 1000px) {
  .entry {
    padding: 35px 0 20px;

    &__wrapper {
      width: 100%;
      max-width: 1000px;
      margin: 0 auto;
    }

    &__content {
      max-width: 940px;
      margin: 0 30px;
    }

    &__title {
      font-size: 20px;
      line-height: 25px;
      margin-top: 0;
      margin-bottom: 10px;
    }

    &__button {
      width: auto;
      padding-top: 0;

      .button {
        width: 100%;
        min-width: 300px;
      }
    }
  }
}
</style>

<style name="header-hero-handling">
/* Special handling for tablet devices for .header */
.entry.header {
  margin-left: calc(-1 * var(--sm-body-padding));
  margin-right: calc(-1 * var(--sm-body-padding));

  @media (--md) {
    margin-left: calc(-1 * var(--md-body-padding));
    margin-right: calc(-1 * var(--md-body-padding));
  }

  @media (min-width: 1025px) {
    margin-left: calc(-1 * var(--lg-body-padding));
    margin-right: calc(-1 * var(--lg-body-padding));
  }
}
</style>
